import React, { createContext, useContext, useState } from 'react';
import type { HandType, GameType } from '@big2/shared';

export type Language = 'mn' | 'en';

const STORAGE_KEY = 'big2.lang';

function getInitialLanguage(): Language {
  if (typeof localStorage !== 'undefined' && typeof localStorage?.getItem === 'function') {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'mn' || saved === 'en') return saved;
  }
  return 'mn';
}

export interface Translations {
  appTitle: string;
  appSubtitle: string;
  rulesButton: string;
  readRulesCta: string;
  closeRules: string;
  cardBacksButton: string;
  cardBackTitle: string;
  cardBackSubtitle: string;
  cardBackSelected: string;
  cardBackChoose: string;
  cardBackLabel: string;
  changeCardBack: string;
  profileButtonGuest: string;
  profileTitle: string;
  signInTab: string;
  signUpTab: string;
  emailLabel: string;
  passwordLabel: string;
  displayNameLabel: string;
  signInAction: string;
  signUpAction: string;
  signOutAction: string;
  tsaiBalanceLabel: string;
  tsaiUnit: string;
  newUserBonusBanner: string;
  weeklyBonusBanner: string;
  claimWeeklyButton: string;
  weeklyNextAvailable: (dateStr: string) => string;
  weeklyGrantedToast: (amount: number) => string;
  statsWins: (n: number) => string;
  statsGames: (n: number) => string;
  statsWinRate: (rate: string) => string;
  levelLabel: (lvl: number) => string;
  totalXpLabel: (xp: number) => string;
  nextLevelHint: (remaining: number) => string;
  friendsTab: string;
  addFriend: string;
  friendsList: string;
  incomingFriendRequests: string;
  outgoingFriendRequests: string;
  noFriendsYet: string;
  noIncomingRequests: string;
  noOutgoingRequests: string;
  acceptFriend: string;
  rejectFriend: string;
  removeFriend: string;
  cancelRequest: string;
  addFriendPlaceholder: string;
  onlineStatus: string;
  offlineStatus: string;
  inRoomStatus: (roomId: string) => string;
  friendRequestSent: string;
  gameModeLabel: string;
  modeFreeTitle: string;
  modeFreeDesc: string;
  modeTsaiTitle: string;
  modeTsaiDesc: (minTsai: number) => string;
  minTsaiRequiredBadge: (minTsai: number) => string;
  tsaiSignInPrompt: string;
  tsaiInsufficientPrompt: (need: number, have: number) => string;
  connected: (ms: number | string) => string;
  connecting: string;
  disconnected: string;
  connectionError: string;
  yourName: string;
  namePlaceholder: string;
  quickPlay: string;
  quickPlayDesc: string;
  createNewRoom: string;
  or: string;
  roomCodePlaceholder: string;
  join: string;
  roomLabel: string;
  copyCode: string;
  copiedCode: string;
  copyLink: string;
  copiedLink: string;
  clickToCopy: string;
  shareCodeHint: string;
  wonRound: (name: string) => string;
  playersHeader: (count: number, max: number) => string;
  youSuffix: string;
  offline: string;
  botTakeover: string;
  abandoned: string;
  matchInProgress: string;
  matchInProgressDesc: (roomId: string) => string;
  rejoinMatch: string;
  abandonMatch: string;
  totalScore: (score: number) => string;
  settingsHeader: string;
  roomSize: string;
  playersCount: (n: number) => string;
  fillWithBots: string;
  botDifficulty: string;
  botDifficultyEasy: string;
  botDifficultyMedium: string;
  botDifficultyHard: string;
  leave: string;
  startGame: string;
  matchLoadingStarting: string;
  matchLoadingGetReady: string;
  matchLoadingGo: string;
  matchLoadingRound: (round: number) => string;
  matchLoadingSkip: string;
  matchLoadingTipLabel: string;
  matchLoadingPreparing: string;
  matchLoadingShuffling: string;
  matchLoadingReady: string;
  matchLoadingTips: Record<GameType, string[]>;
  playAgain: string;
  startNewMatch: string;
  nextRound: string;
  resetPoints: string;
  passBadge: string;
  trickFrom: (handLabel: string, playerName: string) => string;
  playToStartTrick: string;
  sortLabel: string;
  sortByRank: string;
  sortBySuit: string;
  sortByPairs: string;
  passButton: string;
  playButton: string;
  yourTurn: string;
  waitingForTurn: (name: string) => string;
  turnActive: string;
  nextPlayer: (name: string) => string;
  potCards: (count: number) => string;
  dealerRole: string;
  handOrder: (order: number) => string;
  cardsRatio: (remain: number, total: number) => string;
  cannotBeatTrickPrompt: string;
  passSuggested: string;
  selectCardsToPlay: string;
  invalidCombination: string;
  mustIncludeCard: (card: string) => string;
  mustPlayCount: (count: number) => string;
  doesNotBeatTrick: string;
  handLabels: Record<HandType, string>;
  translateServerError: (msg: string) => string;
  landingHeroBadge: string;
  landingHeroBig2Title: string;
  landingHeroMuushigTitle: string;
  landingHeroSubtitle: string;
  landingCtaPlay: string;
  landingCtaGames: string;
  landingCtaHowToPlay: string;
  landingCtaRules: string;
  landingCtaFaq: string;
  landingTrustInstant: string;
  landingTrustInstantSub: string;
  landingTrustBots: string;
  landingTrustBotsSub: string;
  landingTrustRooms: string;
  landingTrustRoomsSub: string;
  landingTrustTsai: string;
  landingTrustTsaiSub: string;
  landingGamesTitle: string;
  landingGamesSubtitle: string;
  landingBig2Desc: string;
  landingMuushigDesc: string;
  landingPlayBig2: string;
  landingPlayMuushig: string;
  landingBig2RulesBtn: string;
  landingMuushigRulesBtn: string;
  landingStepsTitle: string;
  landingStepsSubtitle: string;
  landingStep1Title: string;
  landingStep1Desc: string;
  landingStep2Title: string;
  landingStep2Desc: string;
  landingStep3Title: string;
  landingStep3Desc: string;
  landingFeaturesTitle: string;
  landingFeaturesSubtitle: string;
  landingFaqTitle: string;
  landingFaqSubtitle: string;
  landingFooterBrandText: string;
  landingFooterCopyright: string;
  continueWithGoogle: string;
  orWithEmail: string;
  guestSaveNotice: string;
}

const mnHandLabels: Record<HandType, string> = {
  single: 'Ганц мод',
  pair: 'Хос',
  triple: 'Гурвал',
  straight: 'Стрейт',
  flush: 'Флаш',
  fullHouse: 'Фулл хаус',
  fourOfAKind: 'Дөрвөл (Каре)',
  straightFlush: 'Стрейт флаш',
};

const enHandLabels: Record<HandType, string> = {
  single: 'Single',
  pair: 'Pair',
  triple: 'Three of a Kind',
  straight: 'Straight',
  flush: 'Flush',
  fullHouse: 'Full House',
  fourOfAKind: 'Four of a Kind',
  straightFlush: 'Straight Flush',
};

const mnServerErrorMap: Record<string, string> = {
  'Please enter a name.': 'Нэрээ оруулна уу.',
  'Please enter a valid email address.': 'Зөв имэйл хаяг оруулна уу.',
  'Password must be at least 6 characters.': 'Нууц үг хамгийн багадаа 6 тэмдэгт байх ёстой.',
  'An account with this email already exists.': 'Энэ имэйл хаягаар бүртгэл аль хэдийн үүссэн байна.',
  'Invalid email or password.': 'Имэйл эсвэл нууц үг буруу байна.',
  'Please sign in first.': 'Эхлээд бүртгэлээрээ нэвтэрнэ үү.',
  'Profile not found.': 'Хэрэглэгчийн профайл олдсонгүй.',
  'Invalid coupon code.': 'Купоны код буруу байна.',
  'Invalid, expired or exhausted coupon.': 'Купон хүчингүй, хугацаа нь дууссан эсвэл таны/нийт ашиглах хязгаарт хүрсэн байна.',
  'Invalid, expired or exhausted payment coupon.': 'Төлбөрийн купон хүчингүй, хугацаа нь дууссан эсвэл таны/нийт ашиглах хязгаарт хүрсэн байна.',
  'Coupon usage limit reached.': 'Энэ купоныг ашиглах хязгаарт хүрсэн байна.',
  'An earlier payment must be completed or reviewed first.': 'Өмнөх төлбөрийг эхлээд дуусгах эсвэл шалгуулах шаардлагатай.',
  'Payment needs support review.': 'Энэ төлбөрийг тусламжийн баг шалгах шаардлагатай.',
  'Weekly Tsai bonus is not ready yet.': '7 хоногийн Цай урамшуулал авах хугацаа хараахан болоогүй байна.',
  'Invalid account tier.': 'Хэрэглэгчийн эрхийн төрөл буруу байна.',
  'Premium emotes are only available for Paid Tier members.':
    'Зөвхөн Төлбөртэй (VIP) эрхтэй тоглогчид тусгай зурагтай эмоци ашиглах боломжтой.',
  'Must be signed in to play in a Token Room.': 'Цайтай өрөөнд тоглохын тулд заавал бүртгүүлж нэвтэрсэн байх шаардлагатай.',
  'You are already seated in this Token Room.': 'Та энэ Цайтай өрөөнд аль хэдийн орсон байна.',
  'All players in the room must be signed in to switch to Token Room mode.':
    'Цайтай өрөө горимд шилжихийн тулд өрөөний бүх тоглогчид бүртгэлээрээ нэвтэрсэн байх ёстой.',
  'Invalid room mode.': 'Өрөөний горим буруу байна.',
  'Please enter an invite code.': 'Өрөөний кодыг эсвэл нэрийг оруулна уу.',
  'Invite codes are 4–6 letters/numbers.': 'Өрөөний код 4–6 үсэг эсвэл тоо байх ёстой.',
  'Invite codes are 2–20 letters/numbers.': 'Өрөөний код эсвэл нэр 2–20 үсэг/тоо байх ёстой.',
  'Room name must be 2–20 letters or numbers.': 'Өрөөний тусгай нэр 2–20 үсэг эсвэл тоо байх ёстой.',
  'Custom room names are only available for Paid Tier members.':
    'Зөвхөн Төлбөртэй эрхтэй (Paid Tier) хэрэглэгчид өрөөнд тусгай нэр өгөх боломжтой.',
  'That room name is already taken. Please choose another name.':
    'Энэ өрөөний нэр аль хэдийн ашиглагдаж байна. Өөр нэр сонгоно уу.',
  'Room size must be 2, 3 or 4.': 'Тоглогчийн тоо 2, 3 эсвэл 4 байх ёстой.',
  'Room size must be 2, 3, 4 or 5.': 'Тоглогчийн тоо 2, 3, 4 эсвэл 5 байх ёстой.',
  'Invalid game type.': 'Тоглоомын төрөл буруу байна.',
  'fillWithBots must be true or false.': 'Ботын тохиргоо буруу байна.',
  'Select between 1 and 5 cards.': '1-ээс 5 хөзөр сонгоно уу.',
  'Select between 0 and 5 cards.': '0-оос 5 хөзөр сонгоно уу.',
  'Select 1 card to play.': 'Гаргах 1 хөзрөө сонгоно уу.',
  'The stock pile is empty – play with 0 cards selected or resign.':
    'Газрын мод дууссан байна – хөзөр сонголгүй тоглох эсвэл өнжинө үү.',
  'You cannot pass during trick-taking in Muushig. Play a card.':
    'Муушиг тоглолтын дундуур өнжих боломжгүй. 1 хөзөр гаргана уу.',
  'All other players resigned – you cannot resign.':
    'Бусад бүх тоглогчид өнжсөн тул та өнжих боломжгүй.',
  'Invalid card code.': 'Хөзрийн код буруу байна.',
  'Slow down – too many requests.': 'Хэт олон хүсэлт илгээгдлээ, түр хүлээнэ үү.',
  'You are already in this room.': 'Та аль хэдийн энэ өрөөнд байна.',
  'You are not in a room.': 'Та өрөөнд ороогүй байна.',
  'Room not found. Check the invite code.': 'Өрөө олдсонгүй. Өрөөний кодоо шалгана уу.',
  'A game is already in progress in this room.': 'Энэ өрөөнд тоглоом аль хэдийн эхэлсэн байна.',
  'This room is full.': 'Энэ өрөө дүүрсэн байна.',
  'Only the host can change room settings.': 'Зөвхөн өрөө үүсгэгч тохиргоог өөрчлөх эрхтэй.',
  'Cannot change settings during a game.': 'Тоглоомын дундуур тохиргоо өөрчлөх боломжгүй.',
  'More players are seated than that room size allows.': 'Сонгосон суудлын тооноос олон тоглогч байна.',
  'Only the host can start the game.': 'Зөвхөн өрөө үүсгэгч тоглоомыг эхлүүлэх эрхтэй.',
  'The game has already started.': 'Тоглоом аль хэдийн эхэлсэн байна.',
  'No game in progress.': 'Идэвхтэй тоглоом алга байна.',
  'It is not your turn.': 'Таны ээлж болоогүй байна.',
  'The round is over.': 'Энэ үе дууссан байна.',
  'Duplicate cards submitted.': 'Давхардсан хөзөр сонгогдсон байна.',
  'You do not hold those cards.': 'Танд эдгээр хөзөр байхгүй байна.',
  'That is not a valid combination.': 'Зөвшөөрөгдсөн хослол биш байна.',
  'You are leading – you cannot pass.': 'Та эхэлж гаргах ээлжтэй тул өнжих боломжгүй.',
  'The game is paused.': 'Тоглоом түр зогссон байна.',
  'The game is already paused.': 'Тоглоом аль хэдийн түр зогссон байна.',
  'No pause request is active.': 'Түр зогсоох идэвхтэй санал алга байна.',
  'The game is not paused.': 'Тоглоом түр зогсоогүй байна.',
  'Invalid pause action.': 'Буруу үйлдэл байна.',
  'Invalid reaction.': 'Буруу эмоци байна.',
  'Cannot add yourself as a friend.': 'Өөрийгөө найзаар нэмэх боломжгүй.',
  'User not found.': 'Хэрэглэгч олдсонгүй.',
  'You are already friends with this player.': 'Та энэ тоглогчтой аль хэдийн найз болсон байна.',
  'Friend request already sent.': 'Найзын хүсэлт аль хэдийн илгээгдсэн байна.',
  'Friendship not found.': 'Найз олдсонгүй.',
  'Friend request not found or already processed.': 'Найзын хүсэлт олдсонгүй эсвэл шийдвэрлэгдсэн байна.',
  'Please specify a player to add.': 'Нэмэх тоглогчоо сонгоно уу.',
  'Voice chat rooms are only available for Paid Tier members.':
    'Дуут яриатай өрөө үүсгэх эрх зөвхөн Төлбөртэй (VIP) эрхтэй хэрэглэгчдэд нээлттэй.',
  'Voice chat is not enabled in this room.': 'Энэ өрөөнд дуут яриа идэвхгүй байна.',
  'Please verify your email address before signing in.':
    'Нэвтрэхийн өмнө имэйл хаягаа баталгаажуулна уу.',
  'Invalid or expired verification code.':
    'Баталгаажуулах код буруу эсвэл хугацаа нь дууссан байна.',
  'Invalid or expired verification link.':
    'Баталгаажуулах холбоос буруу эсвэл хугацаа нь дууссан байна.',
  'Invalid or expired password reset code.':
    'Нууц үг сэргээх код буруу эсвэл хугацаа нь дууссан байна.',
  'Invalid or expired password recovery session.':
    'Нууц үг сэргээх холбоос буруу эсвэл хугацаа нь дууссан байна.',
  'Current password is incorrect.': 'Одоогийн нууц үг буруу байна.',
  'Please wait a minute before requesting another verification email.':
    'Баталгаажуулах имэйл дахин илгээхийн өмнө 1 минут хүлээнэ үү.',
  'Please wait a minute before requesting another password reset email.':
    'Нууц үг сэргээх имэйл дахин илгээхийн өмнө 1 минут хүлээнэ үү.',
  'Please sign in again.': 'Дахин нэвтэрнэ үү.',
  'Free throw quota reached for this round. Upgrade to VIP for unlimited throws!':
    'Энэ үеийн үнэгүй шидэлтийн эрх дууссан байна. Хязгааргүй шидэхийн тулд VIP эрхээ идэвхжүүлнэ үү!',
  'Free voice taunt quota reached for this round. Upgrade to VIP for unlimited taunts!':
    'Энэ үеийн үнэгүй тавлалтын эрх дууссан байна. Хязгааргүй тавлахын тулд VIP эрхээ идэвхжүүлнэ үү!',
  'Please wait a moment before sending another interaction.':
    'Дахин илгээхийн өмнө түр хүлээнэ үү.',
  'Cannot throw at yourself.':
    'Өөрийнхөө суудал руу шидэх боломжгүй.',
  'Target player not found.':
    'Зорилтот тоглогч олдсонгүй.',
  'Sender not in room.':
    'Та өрөөнд ороогүй байна.',
  'Invalid throwable.':
    'Шидэх зүйл буруу байна.',
  'Invalid voice taunt.':
    'Тавлалтын дуу буруу байна.',
  'Invalid target seat.':
    'Сонгосон суудал буруу байна.',
  'You were removed from the room by the host.':
    'Өрөөний эзэн таныг өрөөнөөс гаргалаа.',
  'You were removed from this room. Please wait before rejoining.':
    'Та энэ өрөөнөөс хасагдсан байна. Түр хүлээнэ үү.',
  'Players can only be removed while in the lobby.':
    'Зөвхөн лоббид байх үед тоглогчийг өрөөнөөс гаргах боломжтой.',
  'Only the room host can remove players.':
    'Зөвхөн өрөөний эзэн тоглогчийг өрөөнөөс гаргах эрхтэй.',
  'The room host cannot be removed.':
    'Өрөөний эзнийг өрөөнөөс гаргах боломжгүй.',
  'Unexpected server error.': 'Серверийн алдаа гарлаа.',
};

function translateMnServerError(msg: string): string {
  if (mnServerErrorMap[msg]) return mnServerErrorMap[msg];
  const failedAttemptsMatch = msg.match(/^Too many failed attempts\. Please wait (\d+) seconds\.$/);
  if (failedAttemptsMatch) {
    return `Хэт олон удаа буруу код орууллаа. ${failedAttemptsMatch[1]} секунд хүлээнэ үү.`;
  }
  const stockMatch = msg.match(/^Only (\d+) card\(s\) left in the stock pile\.$/);
  if (stockMatch) {
    return `Газарт зөвхөн ${stockMatch[1]} мод үлдсэн байна.`;
  }
  const suitMatch = msg.match(/^You must follow the led suit \((.+)\)\.$/);
  if (suitMatch) {
    return `Та гарсан модны өнгийг (${suitMatch[1]}) дагаж гаргах ёстой!`;
  }
  const tsaiMatch = msg.match(/^Not enough Tsai\. You need at least (\d+) Tsai to play \(you have (\d+)\)\.$/);
  if (tsaiMatch) {
    return `Цай хүрэлцэхгүй байна! Тоглоход доод тал нь ${tsaiMatch[1]} 🍵 Цай шаардлагатай (Танд ${tsaiMatch[2]} 🍵 байна).`;
  }
  const tokenRoomWait = msg.match(/^Waiting for (\d+) more player\(s\) to start the Token Room\.$/);
  if (tokenRoomWait) {
    return `Цайтай өрөө эхлүүлэхэд дахин ${tokenRoomWait[1]} тоглогч дутуу байна (Цайтай өрөөнд бот тоглохгүй).`;
  }
  const waitMatch = msg.match(/^Waiting for (\d+) more player\(s\)/);
  if (waitMatch) {
    return `Дахин ${waitMatch[1]} тоглогч хүлээж байна, эсвэл "Сул суудлыг ботоор дүүргэх" сонголтыг идэвхжүүлнэ үү.`;
  }
  const openMatch = msg.match(/^The opening play must include ([A-Z0-9]+)\.$/);
  if (openMatch) {
    return `Эхний гараанд заавал ${openMatch[1]} хөзрийг оролцуулах ёстой.`;
  }
  const countMatch = msg.match(/^You must play (\d+) card\(s\)\.$/);
  if (countMatch) {
    return `Та заавал ${countMatch[1]} хөзөр гаргах ёстой.`;
  }
  if (msg.includes('Free throw quota reached')) {
    return 'Энэ үеийн үнэгүй шидэлтийн эрх дууссан байна. Хязгааргүй шидэхийн тулд VIP эрхээ идэвхжүүлнэ үү!';
  }
  if (msg.includes('Free voice taunt quota reached')) {
    return 'Энэ үеийн үнэгүй тавлалтын эрх дууссан байна. Хязгааргүй тавлахын тулд VIP эрхээ идэвхжүүлнэ үү!';
  }
  if (msg.includes('before sending another interaction')) {
    return 'Дахин илгээхийн өмнө түр хүлээнэ үү.';
  }
  if (msg.includes('does not beat the')) {
    return 'Таны сонгосон мод одоогийн модыг дийлэхгүй байна.';
  }
  return msg;
}

export const DICTIONARY: Record<Language, Translations> = {
  mn: {
    appTitle: 'Арван гурав',
    appSubtitle: 'Онлайн олон тоглогчийн хөзрийн тоглоом',
    rulesButton: 'Дүрэм',
    readRulesCta: '📖 Тоглоомын дүрэм ба заавар унших',
    closeRules: 'Буцах / Хаах',
    cardBacksButton: 'Хөзрийн ар',
    cardBackTitle: 'Хөзрийн ар загвар сонгох',
    cardBackSubtitle: 'Ширээн дээр харагдах хөзрийн ар талын загвараа сонгоно уу.',
    cardBackSelected: '✓ Сонгогдсон',
    cardBackChoose: 'Сонгох',
    cardBackLabel: 'Хөзрийн загвар',
    changeCardBack: 'Загвар солих',
    profileButtonGuest: 'Нэвтрэх · +100 🍵',
    profileTitle: 'Хэрэглэгчийн профайл ба 🍵 Цай',
    signInTab: 'Нэвтрэх',
    signUpTab: 'Шинэ бүртгэл (+100 🍵)',
    emailLabel: 'Имэйл хаяг',
    passwordLabel: 'Нууц үг (6+ тэмдэгт)',
    displayNameLabel: 'Тоглогчийн нэр',
    signInAction: 'Нэвтрэх',
    signUpAction: 'Бүртгүүлээд 100 🍵 Цай авах',
    signOutAction: 'Гарах (Системээс)',
    tsaiBalanceLabel: 'Таны Цайны үлдэгдэл',
    tsaiUnit: 'Цай',
    newUserBonusBanner:
      '🎁 Шинэ хэрэглэгч бүрт 100 🍵 Цай үнэгүй! 7 хоног бүр Үнэгүй эрх +40 🍵, Төлбөртэй эрх +160 🍵 Цай авна.',
    weeklyBonusBanner: 'Долоо хоног бүрийн Цай урамшуулал (Үнэгүй: +40 🍵 · Төлбөртэй VIP: +160 🍵)',
    claimWeeklyButton: '🎁 Долоо хоногийн Цай авах',
    weeklyNextAvailable: (dateStr) => `Дараагийн урамшуулал авах хугацаа: ${dateStr}`,
    weeklyGrantedToast: (amount) => `🎉 Долоо хоногийн урамшуулал +${amount} 🍵 Цай нэмэгдлээ!`,
    statsWins: (n) => `🏆 Хожил: ${n}`,
    statsGames: (n) => `🎴 Нийт: ${n}`,
    statsWinRate: (rate) => `🎯 Хожлын хувь: ${rate}%`,
    levelLabel: (lvl) => `Үе: ${lvl}`,
    totalXpLabel: (xp) => `⚡ ${xp} XP`,
    nextLevelHint: (rem) => `Дараагийн түвшин хүртэл: ${rem} XP`,
    friendsTab: 'Найзууд',
    addFriend: 'Найз нэмэх',
    friendsList: 'Найзуудын жагсаалт',
    incomingFriendRequests: 'Ирсэн хүсэлт',
    outgoingFriendRequests: 'Илгээсэн хүсэлт',
    noFriendsYet: 'Одоогоор найз нэмээгүй байна.',
    noIncomingRequests: 'Ирсэн хүсэлт байхгүй.',
    noOutgoingRequests: 'Илгээсэн хүсэлт байхгүй.',
    acceptFriend: 'Зөвшөөрөх',
    rejectFriend: 'Татгалзах',
    removeFriend: 'Найзаас хасах',
    cancelRequest: 'Цуцлах',
    addFriendPlaceholder: 'Тоглогчийн нэр эсвэл имэйл...',
    onlineStatus: 'Онлайн',
    offlineStatus: 'Офлайн',
    inRoomStatus: (roomId: string) => `Өрөөнд тоглож байна (${roomId})`,
    friendRequestSent: 'Найзын хүсэлт амжилттай илгээгдлээ!',
    gameModeLabel: 'Өрөөний горим сонгох',
    modeFreeTitle: '🎮 Чөлөөт өрөө (Free for All)',
    modeFreeDesc: 'Хэн ч (зочин эсвэл бүртгэлтэй) орж тоглох боломжтой. Цай хасагдахгүй.',
    modeTsaiTitle: '🍵 Цайтай өрөө (Token Room)',
    modeTsaiDesc: (minTsai) =>
      `Зөвхөн бүртгэлтэй тоглогчид. Ялагч нь ялагдсан тоглогчдын торгуулийн Цайг хурааж авна. Доод үлдэгдэл: ${minTsai} 🍵 Цай.`,
    minTsaiRequiredBadge: (minTsai) => `Доод тал нь ${minTsai} 🍵 Цай шаардлагатай`,
    tsaiSignInPrompt: 'Цайтай өрөөнд тоглохын тулд бүртгүүлж нэвтэрнэ үү (+100 🍵 Цай үнэгүй).',
    tsaiInsufficientPrompt: (need, have) =>
      `Цай хүрэлцэхгүй байна: доод тал нь ${need} 🍵 Цай хэрэгтэй (танд ${have} 🍵 байна).`,
    connected: (ms) => `Холбогдсон (${ms}мс)`,
    connecting: 'Холбогдож байна...',
    disconnected: 'Сүлжээ тасарсан',
    connectionError: 'Холболтын алдаа',
    yourName: 'Таны нэр',
    namePlaceholder: 'Тоглогч 1',
    quickPlay: '🔍 Онлайн тоглолт хайх',
    quickPlayDesc: 'Нээлттэй ширээ хайж автоматаар холбогдох (Баримжаа: ~15 сек)',
    createNewRoom: '🏠 Шинэ өрөө үүсгэх (Хувийн)',
    or: 'ЭСВЭЛ',
    roomCodePlaceholder: 'ӨРӨӨНИЙ КОД',
    join: 'Нэгдэх',
    roomLabel: 'Өрөөний код:',
    copyCode: '📋 Код хуулах',
    copiedCode: '✓ Хуулагдлаа!',
    copyLink: '🔗 Холбоос хуулах',
    copiedLink: '✓ Холбоос хуулагдлаа!',
    clickToCopy: 'Хуулахын тулд дарна уу',
    shareCodeHint: 'Энэ кодыг хуулж найзууддаа илгээнэ үү.',
    wonRound: (name) => `🏆 ${name} энэ үед яллаа!`,
    playersHeader: (count, max) => `Тоглогчид (${count}/${max})`,
    youSuffix: '(Та)',
    offline: 'Тасарсан',
    botTakeover: 'Бот орлосон',
    abandoned: 'Гарсан',
    matchInProgress: 'Үргэлжилж буй тоглоом байна!',
    matchInProgressDesc: (roomId) => `Та ${roomId} тоот өрөөний тоглоомд буцаж орох уу?`,
    rejoinMatch: 'Буцаж орох',
    abandonMatch: 'Болих',
    totalScore: (score) => `Нийт: ${score}`,
    settingsHeader: 'Тохиргоо',
    roomSize: 'Тоглогчийн тоо',
    playersCount: (n) => `${n} тоглогч`,
    fillWithBots: 'Сул суудлыг ботоор дүүргэх',
    botDifficulty: 'Ботын түвшин',
    botDifficultyEasy: 'Хялбар (Casual)',
    botDifficultyMedium: 'Дунд (Standard)',
    botDifficultyHard: 'Хүнд (Expert)',
    leave: 'Гарах',
    startGame: 'Тоглоом эхлүүлэх',
    matchLoadingStarting: 'Тоглолт эхэлж байна...',
    matchLoadingGetReady: 'Бэлдээрэй!',
    matchLoadingGo: 'ТОГЛООМ ЭХЭЛЛЭЭ!',
    matchLoadingRound: (round) => `${round}-р үе`,
    matchLoadingSkip: 'Алгасах',
    matchLoadingTipLabel: 'Зөвлөгөө',
    matchLoadingPreparing: 'Ширээ бэлтгэж байна...',
    matchLoadingShuffling: 'Хөзөр хольж, тарааж байна...',
    matchLoadingReady: 'Ширээ бэлэн боллоо!',
    matchLoadingTips: {
      big2: [
        '3♠ (Гилийн 3) барьсан тоглогч эхний гаргалтанд заавал оролцуулж эхлүүлнэ.',
        '10 ба түүнээс дээш хөзөртэй үлдвэл торгуулийн оноо 2 дахин үржигдэнэ!',
        'Хамгийн түрүүнд 13 модоо дуусгасан тоглогч өргийн ялагч болно.',
        'Хөзрийн дараалал: 3, 4, 5 ... 10, J, Q, K, A, 2 (2 нь хамгийн хүчтэй мод).',
        'Мастийн хүч: Бундан ♥ > Дөрвөлжин ♦ > Цэцэг ♣ > Гил ♠.',
      ],
      muushig: [
        '15 онооноос эхэлж хамгийн түрүүнд 0 болгосон тоглогч тэмцээнд түрүүлнэ.',
        'Тоглолтонд орсон ч 1 ч гэр авч чадахгүй бол 5 онооны торгууль нэмэгдэнэ.',
        'Хөзөр солихдоо гил болон өндөр давуу модоо хадгалах нь хожих магадлалыг өсгөнө.',
        'Өнжих нь 1 оноо нэмэх боловч 5 онооны том торгуулиас хамгаалж чадна.',
      ],
      twentyone: [
        'Хоёр Туз (Алтан 21) авбал оноо хамаарахгүйгээр шууд банк цохино.',
        '21 онооноос хэтэрвэл автоматаар шатаж ялагдана.',
        'Банкирын хөзрийг тооцоолж эрсдэлээ сайтар удирдаарай.',
      ],
      daaluu: [
        'Ширээн дээрх модоо хоёр гараараа жигд тарааж сайн холиорой.',
        'Даалуун үед хамгийн их цай хураасан тоглогч өргийг тэргүүлнэ.',
        'Цэрэг болон эвхмэл модоо зөв хадгалж тохирох үед нь гаргаарай.',
      ],
    },
    playAgain: 'Дахин тоглох',
    startNewMatch: 'Шинэ тэмцээн',
    nextRound: 'Дараагийн үе',
    resetPoints: 'Оноо тэглэх',
    passBadge: 'ӨНЖСӨН',
    trickFrom: (handLabel, playerName) => `${playerName} — ${handLabel}`,
    playToStartTrick: 'Эхний модыг гаргана уу',
    sortLabel: 'Эрэмбэлэх:',
    sortByRank: 'Тоо',
    sortBySuit: 'Өнгө',
    sortByPairs: 'Хос',
    passButton: 'Өнжих',
    playButton: 'Гаргах',
    yourTurn: 'Таны ээлж',
    waitingForTurn: (name) => `${name}-ийн ээлж...`,
    turnActive: 'Ээлж',
    nextPlayer: (name) => `Дараах: ${name}`,
    potCards: (count) => `Газрын мод: ${count}`,
    dealerRole: 'Тараагч',
    handOrder: (order) => `${order}-р гар`,
    cardsRatio: (remain, total) => `${remain}/${total}`,
    cannotBeatTrickPrompt: 'Дийлэх хөзөр байхгүй — Өнжинө үү',
    passSuggested: 'Өнжих (Дийлэхгүй)',
    selectCardsToPlay: 'Гаргах хөзрөө сонгоно уу',
    invalidCombination: 'Буруу хослол байна',
    mustIncludeCard: (card) => `Заавал ${card} оролцуулна`,
    mustPlayCount: (count) => `${count} хөзөр гаргах ёстой`,
    doesNotBeatTrick: 'Одоогийн модыг дийлэхгүй',
    handLabels: mnHandLabels,
    translateServerError: translateMnServerError,
    landingHeroBadge: '🇲🇳 Монголын #1 Арван гурав Онлайн Төв',
    landingHeroBig2Title: 'Арван гурав Онлайн Тоглоом',
    landingHeroMuushigTitle: 'Монгол Муушиг (Muushig) Онлайн Тоглоом',
    landingHeroSubtitle:
      '13 модоо хамгийн түрүүнд гаргаж ял! Шууд хөтчөөсөө найзуудтайгаа бодит цагт үнэгүй тоглоорой. Эсвэл ухаалаг ботуудтай хүссэн үедээ ур чадвараа сориорой — ямар ч апп татах шаардлагагүй!',
    landingCtaPlay: '🎮 Шууд тоглох',
    landingCtaGames: '🎴 Тоглоомууд',
    landingCtaHowToPlay: '💡 Хэрхэн тоглох вэ?',
    landingCtaRules: '📖 Дүрэм',
    landingCtaFaq: '❓ Асуулт хариулт',
    landingTrustInstant: 'Шууд Хөтчөөсөө',
    landingTrustInstantSub: 'Суулгах шаардлагагүй',
    landingTrustBots: 'Ухаалаг Ботууд',
    landingTrustBotsSub: '24/7 Хэзээ ч бэлэн',
    landingTrustRooms: 'Хувийн Өрөө',
    landingTrustRoomsSub: 'Найзуудаа кодоор урих',
    landingTrustTsai: 'Үнэгүй Цай',
    landingTrustTsaiSub: '+100 Цай бүртгэлд',
    landingGamesTitle: 'Арван гурав — Үндсэн Хөзрийн Тоглоом',
    landingGamesSubtitle: 'Монголын Арван гурав тоглогчдын төв талбар ба нэмэлт тоглоомууд',
    landingBig2Desc:
      '13 модоо хослолоор өрсөлдөгчдөөсөө түрүүлж гаргах сонгодог өрсөлдөөн.',
    landingMuushigDesc:
      '15 онооноос эхлэн хөзрийн өнгөөр гэр авч 0 оноонд хүрэх уламжлалт тоглоом.',
    landingPlayBig2: 'Арван гурав тоглох',
    landingPlayMuushig: 'Муушиг тоглох',
    landingBig2RulesBtn: 'Дэлгэрэнгүй дүрэм',
    landingMuushigRulesBtn: 'Дэлгэрэнгүй дүрэм',
    landingStepsTitle: 'Хэрхэн тоглож эхлэх вэ?',
    landingStepsSubtitle: '3 алхмаар шууд тоглож эхлээрэй',
    landingStep1Title: 'Нэрээ оруулах',
    landingStep1Desc: 'Нэрээ бичээд шууд тоглох эсвэл бүртгүүлэн 100 Цай авна.',
    landingStep2Title: 'Тоглоом ба Горимоо сонгох',
    landingStep2Desc: 'Арван гурав, Муушиг, 21, Даалуугаас сонгоно.',
    landingStep3Title: 'Шууд өрөөндөө тоглох',
    landingStep3Desc: 'Шуурхай тоглох эсвэл кодоор өрөөндөө нэгдэнэ.',
    landingFeaturesTitle: 'Яагаад arvangurav.com гэж?',
    landingFeaturesSubtitle: 'Хамгийн хурдан, найдвартай онлайн хөзрийн талбар',
    landingFaqTitle: 'Түгээмэл асуултууд',
    landingFaqSubtitle: 'Тоглоомын талаарх товч асуулт, хариултууд',
    landingFooterBrandText:
      'Монголын Арван гурав онлайн хөзрийн тоглоомын төв. Найзуудтайгаа эсвэл ухаалаг боттой Арван гурав болон бусад нэмэлт тоглоомыг дэлхийн хаанаас ч үнэгүй тоглоорой.',
    landingFooterCopyright: '© 2026 arvangurav.com. Бүх эрх хуулиар хамгаалагдсан.',
    continueWithGoogle: 'Google-ээр нэвтрэх',
    orWithEmail: 'Эсвэл имэйлээр',
    guestSaveNotice: 'Google-ээр нэвтэрч 100 Цай авч, үр дүнгээ хадгалаарай',
  },
  en: {
    appTitle: 'Big Two',
    appSubtitle: 'Multiplayer Card Game',
    rulesButton: 'Rules',
    readRulesCta: '📖 How to Play & Game Rules',
    closeRules: 'Back / Close',
    cardBacksButton: 'Card Backs',
    cardBackTitle: 'Choose Your Card Back',
    cardBackSubtitle: 'Select the card back style displayed on the table.',
    cardBackSelected: '✓ Selected',
    cardBackChoose: 'Select',
    cardBackLabel: 'Card Back',
    changeCardBack: 'Change Style',
    profileButtonGuest: 'Sign In · +100 🍵',
    profileTitle: 'User Profile & 🍵 Tsai Tokens',
    signInTab: 'Sign In',
    signUpTab: 'Register (+100 🍵)',
    emailLabel: 'Email Address',
    passwordLabel: 'Password (6+ chars)',
    displayNameLabel: 'Display Name',
    signInAction: 'Sign In',
    signUpAction: 'Create Account & Get 100 🍵 Tsai',
    signOutAction: 'Sign Out',
    tsaiBalanceLabel: 'Your Tsai Balance',
    tsaiUnit: 'Tsai',
    newUserBonusBanner:
      '🎁 New users get 100 free 🍵 Tsai on signup! Weekly claim: +40 🍵 (Free Tier) or +160 🍵 (Paid Tier).',
    weeklyBonusBanner: 'Weekly Tsai Allowance (Free: +40 🍵 · Paid VIP: +160 🍵)',
    claimWeeklyButton: '🎁 Claim Weekly Tsai',
    weeklyNextAvailable: (dateStr) => `Next weekly claim available: ${dateStr}`,
    weeklyGrantedToast: (amount) => `🎉 Weekly bonus of +${amount} 🍵 Tsai added to your balance!`,
    statsWins: (n) => `🏆 Wins: ${n}`,
    statsGames: (n) => `🎴 Games: ${n}`,
    statsWinRate: (rate) => `🎯 Win Rate: ${rate}%`,
    levelLabel: (lvl) => `Level ${lvl}`,
    totalXpLabel: (xp) => `⚡ ${xp} XP`,
    nextLevelHint: (rem) => `${rem} XP to next level`,
    friendsTab: 'Friends',
    addFriend: 'Add Friend',
    friendsList: 'Friends List',
    incomingFriendRequests: 'Incoming Requests',
    outgoingFriendRequests: 'Sent Requests',
    noFriendsYet: 'No friends added yet.',
    noIncomingRequests: 'No incoming requests.',
    noOutgoingRequests: 'No sent requests.',
    acceptFriend: 'Accept',
    rejectFriend: 'Decline',
    removeFriend: 'Remove Friend',
    cancelRequest: 'Cancel',
    addFriendPlaceholder: 'Player name or email...',
    onlineStatus: 'Online',
    offlineStatus: 'Offline',
    inRoomStatus: (roomId: string) => `In room (${roomId})`,
    friendRequestSent: 'Friend request sent successfully!',
    gameModeLabel: 'Select Game Mode',
    modeFreeTitle: '🎮 Free for All',
    modeFreeDesc: 'Anyone can join (registered user or guest). No Tsai tokens staked.',
    modeTsaiTitle: '🍵 Tsaitai Oroo (Token Room)',
    modeTsaiDesc: (minTsai) =>
      `Registered users only. Winner collects penalty Tsai from losing players. Minimum required: ${minTsai} 🍵 Tsai.`,
    minTsaiRequiredBadge: (minTsai) => `Minimum ${minTsai} 🍵 Tsai required`,
    tsaiSignInPrompt: 'Please sign in or register (+100 free 🍵 Tsai) to play in a Token Room.',
    tsaiInsufficientPrompt: (need, have) =>
      `Insufficient Tsai: you need at least ${need} 🍵 Tsai to play (you have ${have} 🍵).`,
    connected: (ms) => `Connected (${ms}ms)`,
    connecting: 'Connecting...',
    disconnected: 'Disconnected',
    connectionError: 'Connection Error',
    yourName: 'Your Name',
    namePlaceholder: 'Player 1',
    quickPlay: '🔍 Find Match',
    quickPlayDesc: 'Search and auto-join an open public match (Est. wait: ~15s)',
    createNewRoom: '🏠 Create New Room (Private)',
    or: 'OR',
    roomCodePlaceholder: 'ROOM CODE',
    join: 'Join',
    roomLabel: 'Room Code:',
    copyCode: '📋 Copy Code',
    copiedCode: '✓ Copied!',
    copyLink: '🔗 Copy Invite Link',
    copiedLink: '✓ Link Copied!',
    clickToCopy: 'Click to copy room code',
    shareCodeHint: 'Copy and share this code with your friends.',
    wonRound: (name) => `🏆 ${name} won the round!`,
    playersHeader: (count, max) => `Players (${count}/${max})`,
    youSuffix: '(You)',
    offline: 'Offline',
    botTakeover: 'Bot Takeover',
    abandoned: 'Abandoned',
    matchInProgress: 'Match in Progress!',
    matchInProgressDesc: (roomId) => `You have an active game in room ${roomId}. Rejoin?`,
    rejoinMatch: 'Rejoin Match',
    abandonMatch: 'Abandon',
    totalScore: (score) => `Total: ${score}`,
    settingsHeader: 'Settings',
    roomSize: 'Room Size',
    playersCount: (n) => `${n} Players`,
    fillWithBots: 'Fill empty seats with bots',
    botDifficulty: 'Bot Level',
    botDifficultyEasy: 'Easy (Casual)',
    botDifficultyMedium: 'Medium (Standard)',
    botDifficultyHard: 'Hard (Expert)',
    leave: 'Leave',
    startGame: 'Start Game',
    matchLoadingStarting: 'Match Starting...',
    matchLoadingGetReady: 'Get Ready!',
    matchLoadingGo: 'MATCH START!',
    matchLoadingRound: (round) => `Round ${round}`,
    matchLoadingSkip: 'Skip',
    matchLoadingTipLabel: 'Pro Tip',
    matchLoadingPreparing: 'Preparing table...',
    matchLoadingShuffling: 'Shuffling and dealing...',
    matchLoadingReady: 'Table ready!',
    matchLoadingTips: {
      big2: [
        'The player holding the 3 of Spades (3♠) must include it in the opening play.',
        'Holding 10 or more cards remaining doubles your penalty points!',
        'The first player to discard all 13 cards wins the round.',
        'Rank hierarchy: 3 through King, then Ace, with 2 as the highest rank.',
        'Suit hierarchy: Hearts ♥ > Diamonds ♦ > Clubs ♣ > Spades ♠.',
      ],
      muushig: [
        'The first player to reduce their score from 15 to 0 wins the match.',
        'Staying in without winning any tricks incurs a heavy 5-point penalty.',
        'Preserve your trump cards when deciding which cards to exchange.',
        'Folding adds 1 point, but avoids the risk of a 5-point penalty.',
      ],
      twentyone: [
        'Two Aces make an instant Golden 21 and automatically beats the dealer.',
        'Exceeding 21 points results in an immediate bust.',
        'Assess dealer odds carefully before hitting or standing.',
      ],
      daaluu: [
        'Thoroughly mix the tiles across the table before dealing.',
        'The player collecting the highest tea points leads the room.',
        'Retain your soldier and paired tiles for decisive turns.',
      ],
    },
    playAgain: 'Play Again',
    startNewMatch: 'New Match',
    nextRound: 'Next Round',
    resetPoints: 'Reset Points',
    passBadge: 'PASS',
    trickFrom: (handLabel, playerName) => `${handLabel} from ${playerName}`,
    playToStartTrick: 'Play cards to start trick',
    sortLabel: 'Sort:',
    sortByRank: 'Rank',
    sortBySuit: 'Suit',
    sortByPairs: 'Pairs',
    passButton: 'Pass',
    playButton: 'Play',
    yourTurn: 'Your turn',
    waitingForTurn: (name) => `Waiting for ${name}...`,
    turnActive: 'Turn',
    nextPlayer: (name) => `Next: ${name}`,
    potCards: (count) => `Pot: ${count} cards`,
    dealerRole: 'Dealer',
    handOrder: (order) => (order === 1 ? '1st Hand' : order === 2 ? '2nd Hand' : order === 3 ? '3rd Hand' : `${order}th Hand`),
    cardsRatio: (remain, total) => `${remain}/${total}`,
    cannotBeatTrickPrompt: 'No cards can beat this trick — Please pass',
    passSuggested: 'Pass (Cannot beat)',
    selectCardsToPlay: 'Select cards to play',
    invalidCombination: 'Invalid combination',
    mustIncludeCard: (card) => `Must include ${card}`,
    mustPlayCount: (count) => `Must play ${count} cards`,
    doesNotBeatTrick: 'Does not beat current trick',
    handLabels: enHandLabels,
    translateServerError: (msg) => msg,
    landingHeroBadge: '🏆 The Home of Online Big Two (Arvan Gurav)',
    landingHeroBig2Title: 'Big Two (Arvan Gurav) Online',
    landingHeroMuushigTitle: 'Mongolian Muushig Online',
    landingHeroSubtitle:
      'Shed all 13 cards first to win! Play real-time multiplayer Big Two directly in your browser for free. Challenge friends worldwide or smart bots anytime — no downloads or app installs needed!',
    landingCtaPlay: '🎮 Play Now',
    landingCtaGames: '🎴 Explore Games',
    landingCtaHowToPlay: '💡 How to Play',
    landingCtaRules: '📖 Game Rules',
    landingCtaFaq: '❓ FAQ',
    landingTrustInstant: 'Instant Web Play',
    landingTrustInstantSub: 'No install needed',
    landingTrustBots: 'Smart Bots',
    landingTrustBotsSub: 'Ready 24/7',
    landingTrustRooms: 'Private Rooms',
    landingTrustRoomsSub: 'Invite with code',
    landingTrustTsai: 'Free Tsai Tokens',
    landingTrustTsaiSub: '+100 on sign-up',
    landingGamesTitle: 'Big Two — The Flagship Game',
    landingGamesSubtitle: 'The premier online destination for 13-card Big Two and classic club games',
    landingBig2Desc:
      'Empty your 13-card hand first using singles, pairs, and 5-card poker climbs.',
    landingMuushigDesc:
      'Traditional 32-card trick game: take tricks with trump cards to reach 0 points.',
    landingPlayBig2: 'Play Big Two',
    landingPlayMuushig: 'Play Muushig',
    landingBig2RulesBtn: 'Learn Rules',
    landingMuushigRulesBtn: 'Learn Rules',
    landingStepsTitle: 'How to Play in 3 Easy Steps',
    landingStepsSubtitle: 'Start playing in 3 simple steps',
    landingStep1Title: 'Enter Your Name',
    landingStep1Desc: 'Pick a guest nickname or register to claim 100 free Tsai.',
    landingStep2Title: 'Select Game & Mode',
    landingStep2Desc: 'Choose Big Two, Muushig, 21, or Daaluu mode.',
    landingStep3Title: 'Start Playing',
    landingStep3Desc: 'Quick play with bots or invite friends with a 4-letter code.',
    landingFeaturesTitle: 'Why Players Love arvangurav.com',
    landingFeaturesSubtitle: 'Fast, authentic, and authoritative card gaming',
    landingFaqTitle: 'Frequently Asked Questions',
    landingFaqSubtitle: 'Quick answers to common questions',
    landingFooterBrandText:
      'The premier online destination for Big Two (Arvan Gurav), complete with extra traditional Mongolian card and tile games. Play seamlessly with friends or bots on any device.',
    landingFooterCopyright: '© 2026 arvangurav.com. All rights reserved.',
    continueWithGoogle: 'Continue with Google',
    orWithEmail: 'Or with email',
    guestSaveNotice: 'Sign in with Google to claim 100 Tsai and save your progress',
  },
};

interface I18nContextValue {
  lang: Language;
  setLang: (lang: Language) => void;
  t: Translations;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Language>(getInitialLanguage);

  const setLang = (next: Language) => {
    setLangState(next);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, next);
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = next;
    }
  };

  return (
    <I18nContext.Provider value={{ lang, setLang, t: DICTIONARY[lang] }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

/** Robust clipboard helper with fallback for non-HTTPS or older contexts. */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall back to textarea copy below
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
